"use client";

import { signIn, signOut, useSession } from "next-auth/react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { ShieldOff, UserCog } from "lucide-react";
import {
  checkImpersonationGate,
  prepareImpersonation,
} from "@/actions/impersonation";
import { ImpersonationBanner } from "./impersonation-banner";

export function StaffLanding() {
  const { data: session } = useSession();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vpnGate, setVpnGate] = useState<{
    allowed: boolean;
    ip: string | null;
  } | null>(null);

  useEffect(() => {
    void checkImpersonationGate().then(setVpnGate);
  }, []);

  const isImpersonating = Boolean(session?.impersonatedBy);
  const staffEmail = session?.impersonator?.email ?? session?.user?.email;
  const impersonatedEmail = isImpersonating ? session?.user?.email : null;

  const handleImpersonate = async () => {
    setLoading(true);
    setError(null);
    const result = await prepareImpersonation();
    if (!result.success) {
      setError(result.error);
      setLoading(false);
      return;
    }
    await signIn("whmcs", { callbackUrl: "/" });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <ImpersonationBanner />
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-4 text-center">
        <UserCog className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="mt-4 text-2xl font-bold tracking-tight">
          Support staff
        </h1>
        {isImpersonating ? (
          <p className="mt-2 text-muted-foreground">
            Staff <strong>{staffEmail}</strong> currently impersonating{" "}
            <strong>{impersonatedEmail}</strong>, who has no products. Use{" "}
            &ldquo;Stop impersonating&rdquo; above to return to staff mode.
          </p>
        ) : (
          <p className="mt-2 text-muted-foreground">
            Signed in as <strong>{staffEmail}</strong>. To assist a customer,
            click &ldquo;Login as Owner&rdquo; for that customer in WHMCS first,
            then continue below.
          </p>
        )}
        <div className="mt-6 flex flex-col gap-2">
          {!isImpersonating && vpnGate?.allowed && (
            <button
              type="button"
              onClick={handleImpersonate}
              disabled={loading}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading
                ? "Redirecting…"
                : "Continue as currently impersonated customer (WHMCS)"}
            </button>
          )}
          {!isImpersonating && vpnGate && !vpnGate.allowed && (
            <p className="rounded-md border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              Impersonation is only available from the company VPN. Your current
              IP <strong>{vpnGate.ip ?? "(unknown)"}</strong> is not on the
              allowlist.
            </p>
          )}
          <Link
            href="/staff/reset-2fa"
            className="flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            <ShieldOff className="h-4 w-4" />
            Reset a user&apos;s 2FA
          </Link>
          <button
            type="button"
            onClick={() => signOut({ callbackUrl: "/" })}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Sign out
          </button>
        </div>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      </div>
    </div>
  );
}
